import { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { Address, newAddressProp, User } from '../types/type';
import { useErrorHandler } from '../../utils/useErrorHandler';
import { useApiKey } from './useApiKey';
import dataProvider from '../../services/api';

export const useAddressManagement = (selectedUser: User | null) => {
  const { handleErrorWithLogout, showSuccessMessage, showErrorMessage, snackbar, handleSnackbarClose } = useErrorHandler();
  
  const { apiKeyValue, loading } = useApiKey();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [newAddress, setNewAddress] = useState<newAddressProp>({
    userId: 0,
    addresseeFirstName: '',
    addresseeMiddleName: '',
    addresseeLastName: '',
    coordinatesLatitude: 0,
    coordinatesLongitude: 0,
    unit: '',
    building: '',
    street: '',
    subdivision: '',
    cityId: 0,
    zipCode: '',
  });
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);

  const updateCoordinates = debounce(async (address: string, isNewAddress: boolean) => {
    if (!address || !apiKeyValue || loading) return;

    setIsGeocodingLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}, Philippines&key=${apiKeyValue}`
      );
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        if (isNewAddress) {
          setNewAddress(prev => ({
            ...prev,
            coordinatesLatitude: location.lat,
            coordinatesLongitude: location.lng,
          }));
        } else {
          setSelectedAddress(prev => prev ? ({
            ...prev,
            coordinatesLatitude: location.lat,
            coordinatesLongitude: location.lng,
          } as Address) : null);
        }
        showSuccessMessage(`Coordinates updated successfully.`);
      } else {
        showErrorMessage('No coordinates found for this location');
      }
    } catch (error) {
      handleErrorWithLogout(error);
    } finally {
      setIsGeocodingLoading(false);
    }
  }, 1500);

  const fetchAddresses = async () => {
    if (selectedUser?.userId) {
      try {
        const { data } = await dataProvider.getUserAddresses({ userId: selectedUser.userId });
        setAddresses(data);
        setSelectedAddress(data[0] || null);
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    } else {
      setAddresses([]);
      setSelectedAddress(null);
    }
  };

  const deleteAddress = async () => {
    if (selectedAddress && selectedUser) {
      try {
        await dataProvider.deleteUserAddress({
          userId: selectedUser.userId,
          addressId: selectedAddress.addressId,
        });
        await fetchAddresses();
        showSuccessMessage('Address deleted successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const saveAddress = async () => {
    if (selectedAddress && selectedUser) {
      if (!selectedAddress.city?.cityId) {
        showErrorMessage('Please select a valid city before saving.');
        return;
      }
      try {
        const addressData = {
          userId: selectedUser.userId,
          addressId: selectedAddress.addressId,
          addresseeFirstName: selectedAddress.addresseeFirstName,
          addresseeMiddleName: selectedAddress.addresseeMiddleName,
          addresseeLastName: selectedAddress.addresseeLastName,
          coordinatesLatitude: selectedAddress.coordinatesLatitude,
          coordinatesLongitude: selectedAddress.coordinatesLongitude,
          unit: selectedAddress.unit,
          building: selectedAddress.building,
          street: selectedAddress.street,
          subdivision: selectedAddress.subdivision,
          cityId: selectedAddress.city.cityId,
          zipCode: selectedAddress.zipCode,
        };
  
        await dataProvider.updateUserAddress({
          userId: selectedUser.userId,
          addressId: selectedAddress.addressId,
          data: addressData,
        });
  
        if (selectedAddress.isPrimary) {
          await dataProvider.userSetPrimary({
            userId: selectedUser.userId,
            addressId: selectedAddress.addressId,
          });
        }
  
        await fetchAddresses();
        showSuccessMessage('Address updated successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const saveNewAddress = async (onSuccess?: () => void) => {
    if (!selectedUser) return;

    if (!newAddress.cityId) {
      showErrorMessage('Please select a valid city before saving.');
      return;
    }

    try {
      const addressData = {
        userId: selectedUser.userId,
        addresseeFirstName: newAddress.addresseeFirstName,
        addresseeMiddleName: newAddress.addresseeMiddleName,
        addresseeLastName: newAddress.addresseeLastName,
        coordinatesLatitude: newAddress.coordinatesLatitude,
        coordinatesLongitude: newAddress.coordinatesLongitude,
        unit: newAddress.unit,
        building: newAddress.building,
        street: newAddress.street,
        subdivision: newAddress.subdivision,
        cityId: newAddress.cityId,
        zipCode: newAddress.zipCode,
      };

      await dataProvider.createUserAddress({ data: addressData });
      await fetchAddresses();
      if (onSuccess) onSuccess();
      showSuccessMessage('New address created successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const resetNewAddress = () => {
    setNewAddress({
      userId: 0,
      addresseeFirstName: '',
      addresseeMiddleName: '',
      addresseeLastName: '',
      coordinatesLatitude: 0,
      coordinatesLongitude: 0,
      unit: '',
      building: '',
      street: '',
      subdivision: '',
      cityId: 0,
      zipCode: '',
    });
    setIsGeocodingLoading(false);
  };

  useEffect(() => {
    fetchAddresses();
  }, [selectedUser]);

  return {
    addresses,
    selectedAddress,
    setSelectedAddress,
    newAddress,
    setNewAddress,
    isGeocodingLoading,
    setIsGeocodingLoading,
    updateCoordinates,
    deleteAddress,
    saveAddress,
    saveNewAddress,
    resetNewAddress,
    fetchAddresses,
    snackbar,
    handleSnackbarClose,
    handleErrorWithLogout,
    showSuccessMessage, 
    showErrorMessage,
  };
};